import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ConnectionProvider, useLocalStorage } from '@solana/wallet-adapter-react';
import { WalletProviderBase } from 'cwy-wallet-provider-base';
import type { WalletName } from '@solana/wallet-adapter-base';
import { CWY_RPC_URL } from '../config/cwyConfig';
import { getCwySolanaWalletAdapters } from './solanaAdapters';

function CwyWalletProviderInner({ children }: { children: React.ReactNode }) {
  const wallets = useMemo(() => getCwySolanaWalletAdapters(), []);
  const [walletName, setWalletName] = useLocalStorage<WalletName | null>('walletName', null);
  const adapter = useMemo(
    () => wallets.find((candidate) => candidate.name === walletName) ?? null,
    [wallets, walletName]
  );
  const isUnloadingRef = useRef(false);

  useEffect(() => {
    function handleBeforeUnload() {
      isUnloadingRef.current = true;
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const changeWallet = useCallback(
    (nextWalletName: WalletName | null) => {
      if (walletName === nextWalletName) return;

      // Disconnect previous session, then switch name. Keep select responsive:
      // set the new name immediately so connect flows aren't racing a delayed rename.
      const previous = adapter;
      setWalletName(nextWalletName);
      if (previous) {
        void previous.disconnect().catch(() => undefined);
      }
    },
    [adapter, setWalletName, walletName]
  );

  useEffect(() => {
    if (!adapter) return;

    function handleDisconnect() {
      if (isUnloadingRef.current) return;
      setWalletName(null);
    }

    adapter.on('disconnect', handleDisconnect);
    return () => {
      adapter.off('disconnect', handleDisconnect);
    };
  }, [adapter, setWalletName]);

  const selectWallet = useCallback(
    (name: WalletName) => {
      changeWallet(name);
    },
    [changeWallet]
  );

  return (
    <WalletProviderBase
      wallets={wallets}
      adapter={adapter}
      isUnloadingRef={isUnloadingRef}
      onConnectError={() => changeWallet(null)}
      onSelectWallet={selectWallet}
    >
      {children}
    </WalletProviderBase>
  );
}

export const SolanaWalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const endpoint = useMemo(() => CWY_RPC_URL, []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <CwyWalletProviderInner>{children}</CwyWalletProviderInner>
    </ConnectionProvider>
  );
};
